import { useLingui } from '@lingui/react/macro';
import BanIcon from '~icons/lucide/ban';
import LanguagesIcon from '~icons/lucide/languages';
import MicIcon from '~icons/lucide/mic';
import { useMemo } from 'react';

import { useImageStoreAvailable } from '#features/image-store/hooks/useImageStoreAvailable';
import { To } from '#features/routing';
import { BellIcon, HardDriveIcon, InfoCircleIcon, KeyRoundIcon, UserRoundIcon } from '#features/ui';
import { GamepadIcon, PhoneIcon } from '#features/ui/components/Icon';

export type SettingsSection = {
  /**
   * Last segment of the route. Used to tell which section is open without hardcoding the
   * locale prefix (`/ru/dashboard/settings/security` and `/dashboard/settings/security` both
   * end with `security`).
   */
  slug:
    | 'profile'
    | 'connections'
    | 'security'
    | 'general'
    | 'notifications'
    | 'devices'
    | 'calls'
    | 'blocked'
    | 'storage'
    | 'about';
  name: string;
  hint: string;
  icon: React.ReactNode;
  to: To;
};

const ICON_SIZE = { fontSize: '20px' };

/**
 * Single source of truth for the settings sections: the list on `/dashboard/settings`, the
 * column beside the content on wide screens and the title of the mobile back header all read
 * from here, so a new section is one entry instead of three edits.
 */
export const useSettingsSections = (): SettingsSection[] => {
  const { t } = useLingui();
  const imageStoreAvailable = useImageStoreAvailable();

  return useMemo(
    () =>
      [
        {
          slug: 'profile',
          name: t`dashboard:settings.profile.title`,
          hint: t`Avatar, name, games and interests`,
          icon: <UserRoundIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/profile',
        },
        /*
          Три раздела вместо «Аккаунта» (23.09.2026). В нём жили четыре вопроса сразу, и главный
          из них - «как я вхожу» - был разбросан по трём местам: почта и пароль в «Безопасности»,
          Google под «Способами входа», а Discord, Steam, Epic и Riot среди игровых аккаунтов.
          Порядок - от того, что делает профиль живым, к тому, что трогают редко.
        */
        {
          slug: 'connections',
          name: t`Game accounts`,
          hint: t`Steam, Discord, Riot and more: stats and what others see`,
          icon: <GamepadIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/connections',
        },
        {
          slug: 'security',
          name: t`Sign-in and security`,
          hint: t`Ways to sign in, email, password, deleting the account`,
          icon: <KeyRoundIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/security',
        },
        {
          slug: 'notifications',
          name: t`dashboard:settings.notifications.title`,
          hint: t`Email, browser and Discord alerts`,
          icon: <BellIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/notifications',
        },
        // Сразу за уведомлениями, потому что отвечает на соседний вопрос: уведомления — «узнаю ли
        // я, что меня зовут», проверка — «смогу ли я ответить». Прод за 30 дней: микрофон не дали
        // 113 людям из 634, и две трети из них больше не возвращались.
        {
          slug: 'devices',
          name: t`media:check.title`,
          hint: t`media:check.sectionHint`,
          icon: <MicIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/devices',
        },
        // Кто может до меня дозвониться — за проверкой устройств: та отвечает «услышу ли я звонок»,
        // этот раздел — «кто вообще может мне позвонить». Отдельной строкой, а не внутри
        // уведомлений: выключенные пуши не выключают звонки друзей, и смешивать их нельзя.
        {
          slug: 'calls',
          name: t`Calls and invitations`,
          hint: t`Who can call you and offer to talk`,
          icon: <PhoneIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/calls',
        },
        {
          slug: 'general',
          name: t`Language and time`,
          hint: t`Language, time zone and time format`,
          icon: <LanguagesIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/general',
        },
        // Кого я заблокировал — здесь, а не вкладкой в «Друзьях», как у Telegram, Instagram
        // и X: редкий экран про собственные решения, а не про круг. Перед «О приложении»,
        // потому что это последнее, что касается самого человека; дальше — про приложение.
        {
          slug: 'blocked',
          name: t`dashboard:settings.blocked.title`,
          hint: t`Who you blocked, and where to unblock`,
          icon: <BanIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/blocked',
        },
        // Картинки, сохранённые на устройстве, – как «Данные и память» у Telegram. Только в
        // приложении и только там, где у платформы есть хранилище: в браузере картинками
        // распоряжается сам браузер. После блокировок, потому что это уже про приложение, а не
        // про человека.
        ...(imageStoreAvailable
          ? [
              {
                slug: 'storage',
                name: t`Storage`,
                hint: t`Saved images and data`,
                icon: <HardDriveIcon style={ICON_SIZE} />,
                to: '/{-$locale}/dashboard/settings/storage',
              } satisfies SettingsSection,
            ]
          : []),
        // Holds what the footer used to carry: legal documents, support, licences and
        // the build number. Signed-in users no longer get a footer on the web, and the
        // app has none at all — this is the only place those live now.
        // "About the app", not "About": the bare word is already the profile bio heading,
        // and Lingui keys by source string — reusing it would label this section "О себе".
        {
          slug: 'about',
          name: t`About the app`,
          hint: t`Documents, support and app version`,
          icon: <InfoCircleIcon style={ICON_SIZE} />,
          to: '/{-$locale}/dashboard/settings/about',
        },
      ] satisfies SettingsSection[],
    [t, imageStoreAvailable]
  );
};
